import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList, StructuredData } from 'components/SEO';
import { COMPARISON_SECTIONS, generateComparisonSchema } from './compareData';
import './compare.css';

const COMPARE_HERO_IMAGE =
  'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/articles/faq_hero.png';

const Compare: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Handle sidebar navigation attempts
  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHelmet
        title="Platform Comparison - TradeMagic vs TCGPlayer, Cardsphere & More"
        description="Compare TradeMagic to TCGPlayer, Cardsphere, Facebook Groups, and PucaTrade. See fees, features, and find the best Magic card trading platform for your needs."
        canonical="https://trademagic.gg/compare"
        keywords="TradeMagic vs TCGPlayer, Cardsphere alternative, PucaTrade alternative, Magic card trading platforms, MTG trading comparison, best MTG trading site"
        type="website"
      />

      {/* BreadcrumbList Schema */}
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Platform Comparison', url: 'https://trademagic.gg/compare' },
        ]}
      />

      {/* Comparison Schema */}
      <StructuredData data={generateComparisonSchema()} />

      <div className="compare-layout">
        {/* Mobile Menu Overlay */}
        {isMobile && isMobileMenuOpen && (
          <div
            className="compare-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="compare-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/compare', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div className="compare-main">
            {/* Top Navigation Bar */}
            <TopNavigationBar
              title="Platform Comparison"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            <div className="compare-container">
              {/* Hero Section */}
              <div
                className="compare-hero"
                style={{ backgroundImage: `url(${COMPARE_HERO_IMAGE})` }}
              >
                <div className="compare-hero-overlay" />
                <img
                  src={COMPARE_HERO_IMAGE}
                  alt="Magic Card Trading Platform Comparison"
                  style={{ display: 'none' }}
                />
              </div>

              {/* Page Header */}
              <header className="compare-header">
                <h1 className="compare-title">Magic Card Trading Platform Comparison</h1>
                <p className="compare-subtitle">
                  Compare TradeMagic to TCGPlayer, Cardsphere, Facebook Groups, and more.
                  Find the best platform for your trading needs.
                </p>
              </header>

              {/* Quick Comparison Table */}
              <section className="compare-table-section">
                <h2 className="compare-section-title">Quick Comparison</h2>
                <div className="compare-table-wrapper">
                  <table className="compare-table">
                    <thead>
                      <tr>
                        <th>Feature</th>
                        <th className="compare-highlight">TradeMagic</th>
                        <th>TCGPlayer</th>
                        <th>Cardsphere</th>
                        <th>Facebook</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="compare-feature-name">Transaction Fee</td>
                        <td className="compare-highlight">1-5%</td>
                        <td>11-15%</td>
                        <td>3% + 10% withdrawal</td>
                        <td>Free</td>
                      </tr>
                      <tr>
                        <td className="compare-feature-name">Point System</td>
                        <td className="compare-highlight">&#10003;</td>
                        <td>&#10007;</td>
                        <td>&#10003;</td>
                        <td>&#10007;</td>
                      </tr>
                      <tr>
                        <td className="compare-feature-name">Auto-Matching</td>
                        <td className="compare-highlight">&#10003;</td>
                        <td>&#10007;</td>
                        <td>&#10003;</td>
                        <td>&#10007;</td>
                      </tr>
                      <tr>
                        <td className="compare-feature-name">Trade Protection</td>
                        <td className="compare-highlight">&#10003;</td>
                        <td>&#10003;</td>
                        <td>&#10003;</td>
                        <td>&#10007;</td>
                      </tr>
                      <tr>
                        <td className="compare-feature-name">Instant Cash Out</td>
                        <td className="compare-highlight">N/A</td>
                        <td>&#10003;</td>
                        <td>Limited</td>
                        <td>&#10003;</td>
                      </tr>
                      <tr>
                        <td className="compare-feature-name">Economic Stability</td>
                        <td className="compare-highlight">Sustainable</td>
                        <td>N/A</td>
                        <td>Working</td>
                        <td>N/A</td>
                      </tr>
                      <tr>
                        <td className="compare-feature-name">Best For</td>
                        <td className="compare-highlight">Regular traders</td>
                        <td>Sellers</td>
                        <td>Active traders</td>
                        <td>Casual trades</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Detailed Platform Sections */}
              {COMPARISON_SECTIONS.map((section) => (
                <section key={section.platform} className="compare-platform-section" id={section.platform.toLowerCase().replace(/\s+/g, '-')}>
                  <div className="compare-platform-header">
                    <h2 className="compare-platform-title">{section.platform}</h2>
                    {section.status && (
                      <span className={`compare-status compare-status-${section.status.toLowerCase()}`}>
                        {section.status}
                      </span>
                    )}
                  </div>

                  <div className="compare-platform-content">
                    <div
                      className="compare-overview"
                      dangerouslySetInnerHTML={{ __html: section.overview }}
                    />

                    <div className="compare-pros-cons">
                      <div className="compare-pros">
                        <h3>Pros</h3>
                        <ul>
                          {section.pros.map((pro, index) => (
                            <li key={index}>{pro}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="compare-cons">
                        <h3>Cons</h3>
                        <ul>
                          {section.cons.map((con, index) => (
                            <li key={index}>{con}</li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {section.bestFor && (
                      <div className="compare-best-for">
                        <h3>Best For:</h3>
                        <p>{section.bestFor}</p>
                      </div>
                    )}

                    {section.migration && (
                      <div className="compare-migration">
                        <h3>How to Migrate to TradeMagic:</h3>
                        <div dangerouslySetInnerHTML={{ __html: section.migration }} />
                      </div>
                    )}
                  </div>
                </section>
              ))}

              {/* Which Platform Section */}
              <section className="compare-recommendation-section">
                <h2 className="compare-section-title">Which Platform is Right for You?</h2>

                <div className="compare-recommendation-grid">
                  <div className="compare-recommendation-card compare-card-trademagic">
                    <h3>Choose TradeMagic if:</h3>
                    <ul>
                      <li>You trade regularly (5+ trades per month)</li>
                      <li>You're a player building EDH/Commander decks</li>
                      <li>You want sustainable economics (no inflation)</li>
                      <li>You prefer automated matching</li>
                      <li>You trade for specific printings/versions</li>
                      <li>You want lower fees than TCGPlayer</li>
                    </ul>
                  </div>

                  <div className="compare-recommendation-card">
                    <h3>Choose TCGPlayer if:</h3>
                    <ul>
                      <li>You need instant cash-out</li>
                      <li>You're selling, not trading</li>
                      <li>You have high-volume inventory to sell</li>
                      <li>You prefer traditional marketplace model</li>
                      <li>You don't mind 11-15% fees</li>
                    </ul>
                  </div>

                  <div className="compare-recommendation-card">
                    <h3>Choose Cardsphere if:</h3>
                    <ul>
                      <li>You're an extremely active trader</li>
                      <li>You keep a close eye on card prices</li>
                      <li>You're comfortable with an established platform</li>
                      <li>You don't need the latest features</li>
                      <li>You prefer a proven, stable system</li>
                    </ul>
                  </div>

                  <div className="compare-recommendation-card">
                    <h3>Choose Facebook Groups if:</h3>
                    <ul>
                      <li>You only trade occasionally</li>
                      <li>You prefer direct communication</li>
                      <li>You don't mind extra friction</li>
                      <li>You're willing to take on more risk</li>
                      <li>You trade locally in-person</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* CTA Section */}
              <div className="compare-cta-section">
                <h2 className="compare-cta-title">Ready to Try TradeMagic?</h2>
                <p className="compare-cta-description">
                  Join hundreds of Magic players trading cards efficiently on TradeMagic.
                  Lower fees than TCGPlayer, sustainable economics unlike PucaTrade, and
                  automated matching for effortless trading.
                </p>
                <button
                  className="compare-cta-button"
                  onClick={() => navigate('/welcome')}
                >
                  Get Started Free
                </button>
                <p className="compare-cta-subtext">
                  No credit card required - Import your collection in minutes
                </p>
              </div>
            </div>
          </div>
        </div>

        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />

        <Footer />
      </div>
    </>
  );
};

export default Compare;
